import promotionState, { initialPromotionState } from '@/states/promotionState';
import api from '@/utils/api';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import { ApiUrl, Constants } from '@/utils/constants';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import { Promotion, PromotionInfoResponse } from 'types/api/mypage/promotionInfo';
import url from '@/utils/config';
import useCommonApiErrorHandling from '../useCommonApiErrorHandling';
import useInsertServiceIdInApiUrl from '../useInsertServiceIdInApiUrl';
import useSetLoading from '../useSetLoading';
import useServiceType from '../useServiceType';

/**
 * 販促情報取得APIの呼び出しフック。
 * APIレスポンスを固定回線/モバイルそれぞれの表示順に整形し、現在値と初期値を保持する。
 *
 * @returns execute - API実行関数
 */
const useGetPromotionSetting = () => {
  /** 販促情報取得APIのURL */
  const apiUrl = ApiUrl.Read.PROMOTION_V2;

  // hooks
  const serviceType = useServiceType();
  const { startLoading, finishLoading } = useSetLoading();
  const { insertServiceIdInApiUrl } = useInsertServiceIdInApiUrl();
  const { commonErrorHandling, commonResponseCodeHandling } = useCommonApiErrorHandling();
  const setPromotion = useSetRecoilState(promotionState);
  const setInitialPromotion = useSetRecoilState(initialPromotionState);

  const execute = useCallback(async () => {
    // 契約サービスごとに配信設定が異なるため、serviceId付きURLで取得する。
    const apiUrlWithServiceId = insertServiceIdInApiUrl(apiUrl);
    const headers = await getSigV4HeadersForGet(apiUrlWithServiceId);

    startLoading(apiUrlWithServiceId);

    await api
      .get(apiUrlWithServiceId, headers)
      .then(({ data }: AxiosResponse<PromotionInfoResponse, any>) => {
        const { isError } = commonResponseCodeHandling({
          responseCode: data.response_code,
          isTransitionTop: true,
        });

        if (isError) return;

        const { promotions } = data;
        let newPromotions: Promotion[] = [];

        const isEnabledPromotionType = (type: string) => {
          // レスポンスに配信種類がない場合、受け取らないをセット
          return promotions.find((item) => item.type === type)?.is_enabled || false;
        };

        if (serviceType === 'fixed') {
          // 画面表示順を固定し、APIに欠けている種類も明示的にfalseで補完する。
          newPromotions = [
            {
              type: Constants.Promotion.Type.UP_SELL_MAIL,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.UP_SELL_MAIL),
            },
            {
              type: Constants.Promotion.Type.UP_SELL_SMS,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.UP_SELL_SMS),
            },
            {
              type: Constants.Promotion.Type.BENEFIT_MAIL,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.BENEFIT_MAIL),
            },
            {
              type: Constants.Promotion.Type.BENEFIT_SMS,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.BENEFIT_SMS),
            },
            {
              type: Constants.Promotion.Type.CROSS_SELL_MAIL,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.CROSS_SELL_MAIL),
            },
            {
              type: Constants.Promotion.Type.CROSS_SELL_SMS,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.CROSS_SELL_SMS),
            },
          ];
        }

        if (serviceType === 'mobile') {
          // モバイルは郵送選択肢を含むため、固定回線とは別の並びで組み立てる。
          newPromotions = [
            {
              type: Constants.Promotion.Type.UP_SELL_MAIL,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.UP_SELL_MAIL),
            },
            {
              type: Constants.Promotion.Type.UP_SELL_POST,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.UP_SELL_POST),
            },
            {
              type: Constants.Promotion.Type.BENEFIT_MAIL,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.BENEFIT_MAIL),
            },
            {
              type: Constants.Promotion.Type.BENEFIT_POST,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.BENEFIT_POST),
            },
            {
              type: Constants.Promotion.Type.CROSS_SELL_MAIL,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.CROSS_SELL_MAIL),
            },
            {
              type: Constants.Promotion.Type.CROSS_SELL_POST,
              is_enabled: isEnabledPromotionType(Constants.Promotion.Type.CROSS_SELL_POST),
            },
          ];
        }

        // 変更前後比較に使うため、現在値と初期値の両方へ同じ内容を保存する。
        setPromotion(newPromotions);
        setInitialPromotion(newPromotions);
      })
      .catch((error: any) => {
        // ログイントークンの有効期限が切れていたら、ログイン画面に遷移
        if (isExpiredToken(error)) {
          sessionStorage.clear();
          window.location.href = url('/login');
          return;
        }

        commonErrorHandling({ error, isTransitionTop: true });
      })
      .finally(() => finishLoading(apiUrlWithServiceId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { execute };
};

export default useGetPromotionSetting;
